import { useMutation, useQueryClient, type QueryKey } from 'react-query';
import {
  createLoungeMessage,
  type CreateLoungeMessageBody,
  type CreateLoungeMessageResponse
} from '../../services/loungeService';
import type { components } from '../../../../types/schema';

type PostPreview = Partial<components['schemas']['PostPreview']>;
type InfinitePosts = {
  pages: Array<{ posts: PostPreview[] } & Record<string, unknown>>;
  pageParams?: unknown[];
};

export function useCreateLoungeMessage() {
  const queryClient = useQueryClient();

  return useMutation<
    CreateLoungeMessageResponse,
    Error,
    CreateLoungeMessageBody,
    {
      snapshots: Array<{ queryKey: QueryKey; previousData?: InfinitePosts }>;
    }
  >({
    mutationFn: (body) => createLoungeMessage(body),
    onMutate: async (body) => {
      // 포토카드가 있는 경우 낙관적 업데이트를 하지 않음
      const optimisticId = `temp-${Date.now()}`;

      await queryClient.cancelQueries(['Posts'], { exact: false });
      const snapshots: Array<{ queryKey: QueryKey; previousData?: InfinitePosts }> = [];

      const queries = queryClient.getQueriesData<InfinitePosts>({ queryKey: ['Posts'] });

      queries.forEach(([qk, data]) => {
        const queryKey = qk as QueryKey;
        snapshots.push({ queryKey, previousData: data });

        const optimisticPost: PostPreview = {
          postId: optimisticId,
          content: body?.post?.content ?? '',
          authoredTimestamp: Math.floor(Date.now() / 1000),
          isMine: true,
          anonymous: !!body?.post?.anonymous,
          authorName: '',
          authorThumbnailUrl: '',
          userProfileId: '',
          isFollowing: false,
          likesCount: 0,
          commentsCount: 0,
          isLiked: false,
          postType: 'post',
          pictureUrls: body?.post?.data_urls ?? [],
          photoCard: body?.post?.photo_card_id
            ? ({
                photoCardId: 'temp',
                smallThumbnailUrl: '',
                pictureUrl: '',
                memberName: '',
                categoryName: '',
                categoryEmoji: '',
                eventName: ''
              } as unknown as components['schemas']['PhotoCard'])
            : undefined
        };

        queryClient.setQueryData<InfinitePosts>(queryKey, (old) => {
          if (old?.pages?.length) {
            const pages = old.pages.map((page, idx) => {
              if (idx !== 0 || !('posts' in page)) return page;
              const existingPosts = Array.isArray((page as { posts?: unknown[] }).posts)
                ? ((page as { posts?: unknown[] }).posts as PostPreview[])
                : [];
              return { ...page, posts: [optimisticPost, ...existingPosts] };
            });
            return { ...old, pages };
          }
          return {
            pages: [{ posts: [optimisticPost] }],
            pageParams: []
          } as InfinitePosts;
        });
      });

      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      context?.snapshots?.forEach(({ queryKey, previousData }) => {
        if (previousData) {
          queryClient.setQueryData(queryKey, previousData);
        } else {
          queryClient.invalidateQueries(queryKey);
        }
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(['Posts']);
    }
  });
}
